import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Alert,
} from "@mui/material";

import CheckCircle from "@mui/icons-material/CheckCircle";
import Info from "@mui/icons-material/Info";
import Timer from "@mui/icons-material/Timer";
import VolumeUp from "@mui/icons-material/VolumeUp";
import NavigateNext from "@mui/icons-material/NavigateNext";
import NavigateBefore from "@mui/icons-material/NavigateBefore";
import Warning from "@mui/icons-material/Warning";
import Highlight from "@mui/icons-material/Highlight";

import React, { useEffect, useRef, useState } from "react";

const AUTO_CLOSE_SECS = 10;

const InstructionModal = ({
  open,
  onClose,
  onNext,
  section,
  mode = "simulation", // 'simulation' or 'practice'
  part = null, // 'A', 'B', 'C' for specific parts
  onAutoClose, // optional callback for auto-close event
}) => {
  // Timer state for auto-close
  const [countdown, setCountdown] = useState(AUTO_CLOSE_SECS);
  const timerRef = useRef(null);

  // Auto-close for all instruction popups (10 seconds)
  const shouldAutoClose = open;

  useEffect(() => {
    if (shouldAutoClose) {
      setCountdown(AUTO_CLOSE_SECS);
      timerRef.current = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timerRef.current);
            if (onAutoClose) onAutoClose();
            else if (onClose) onClose();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else {
      setCountdown(AUTO_CLOSE_SECS);
      if (timerRef.current) clearInterval(timerRef.current);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [shouldAutoClose, open, onAutoClose, onClose]);

  const getInstructionContent = () => {
    const isSimulation = mode === "simulation";

    switch (section) {
      case "tutorial":
        return {
          title: "Welcome to Acadimyst LMS – Test Environment Tutorial",
          content: (
            <Box>
              <Typography variant="body1" paragraph>
                This tutorial is designed to help you become familiar with the
                computer-based OET testing environment on Acadimyst LMS. In this
                tutorial, you will learn how to:
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircle color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Understand the structure of the Online OET Test" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircle color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Navigate through the test interface" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircle color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Use audio playback controls" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircle color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Highlight important text" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircle color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Access and use the integrated PDF viewer" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircle color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Optimize your display settings for Acadimyst LMS" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircle color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Answer multiple-choice questions" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircle color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Properly end each test section" />
                </ListItem>
              </List>

              <Alert severity="warning" sx={{ mt: 2, mb: 2 }}>
                <Typography variant="body2">
                  <strong>Please Note:</strong>
                  <br />• Do not click 'Start the Test' until you have completed
                  the entire tutorial.
                  <br />• Clicking 'Start the Test' will immediately begin the
                  Listening section.
                </Typography>
              </Alert>

              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>Timer Information:</strong>
                  <br />• A timer is displayed at the top of the screen, showing
                  the remaining time for the current section, including the
                  tutorial.
                  <br />• Time alerts will be provided at 30 minutes, 15
                  minutes, and 5 minutes before the end of each applicable
                  section.
                </Typography>
              </Alert>

              <Typography variant="body1">
                When you're ready, click the 'Next' button to proceed.
              </Typography>
            </Box>
          ),
        };

      case "listening":
        if (!part) {
          return {
            title: "Section: Listening Test Introduction – Acadimyst LMS",
            content: (
              <Box>
                <Typography variant="body1" paragraph>
                  You are about to begin the Listening sub-test, which will take
                  approximately 40 minutes to complete.
                </Typography>

                <List dense>
                  <ListItem>
                    <ListItemIcon>
                      <VolumeUp color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Once you click 'Next', the audio will start playing automatically."
                      secondary={
                        isSimulation
                          ? "You cannot pause, rewind, or replay the recording."
                          : "You can pause, stop, rewind or replay the recordings."
                      }
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <Info color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="Part A: After each section, use any remaining time to review your answers." />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <NavigateNext color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        isSimulation
                          ? "Part B: Once you complete a question, you cannot return to it."
                          : "Part B: Once you complete a question, you can return to it."
                      }
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <Info color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="Part C: Use any remaining time at the end of each section to check your responses." />
                  </ListItem>
                </List>

                <Alert
                  severity={isSimulation ? "warning" : "info"}
                  sx={{ mt: 2, mb: 2 }}
                >
                  <Typography variant="body2">
                    You may click 'Next' to move forward at any point, but
                    please note:
                    <br />
                    You {isSimulation ? "cannot" : "can"} go back to a previous
                    section once you proceed ({mode} mode).
                  </Typography>
                </Alert>

                <Box sx={{ mt: 2, p: 2, bgcolor: "grey.100", borderRadius: 1 }}>
                  <Typography variant="body2">
                    • Each correct answer is awarded one mark.
                    <br />• No marks are deducted for incorrect answers —
                    attempt all questions.
                    <br />• Please wait patiently; the Listening test may take a
                    few moments to load.
                  </Typography>
                </Box>

                <Typography variant="body1" sx={{ mt: 2 }}>
                  When ready, click 'Next', then confirm by selecting 'Yes, I
                  would like to finish this section' to begin the Listening
                  test.
                </Typography>
              </Box>
            ),
          };
        }

        if (part === "A") {
          return {
            title: "Part A – Note Completion",
            content: (
              <Box>
                <Typography variant="h6" gutterBottom>
                  Occupational English Test – Listening Sub-Test (Acadimyst LMS
                  - With Audio)
                </Typography>
                <Typography variant="body1" paragraph>
                  The Listening Test consists of three parts, and you will hear
                  a range of different audio extracts.
                </Typography>
                <Typography variant="body1" paragraph>
                  At the beginning of each extract, you will hear a beep sound
                  indicating that the recording is about to begin.
                </Typography>

                <Alert severity="error" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    <strong>Important:</strong> You will hear each extract only
                    once.
                    <br />
                    Complete your answers as you listen — there is no replay
                    option.
                    <br />
                    You will be given time to read the questions before each
                    extract begins.
                  </Typography>
                </Alert>

                <Divider sx={{ my: 2 }} />

                <Typography variant="h6" gutterBottom>
                  Part A – Note Completion
                </Typography>
                <Typography variant="body1" paragraph>
                  You will hear two extracts where a healthcare professional is
                  speaking with a patient.
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircle color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="Your task is to complete the notes with the information you hear." />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <Timer color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="Use any remaining time to review your answers before moving on." />
                  </ListItem>
                </List>

                <Typography variant="body1" sx={{ mt: 2 }}>
                  Now, take a moment to look at the notes for Extract One.
                  <br />
                  When you're ready, click the 'Next' button to proceed to the
                  Listening Part A.
                </Typography>
              </Box>
            ),
          };
        }

        if (part === "B") {
          return {
            title: "Part B – Multiple Choice (Healthcare Settings)",
            content: (
              <Box>
                <Typography variant="body1" paragraph>
                  You will hear six short extracts, each set in a different
                  healthcare context.
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircle color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="For each extract, choose the best answer (A, B, or C) based on what you hear." />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <Timer color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="You'll have time to read each question before the audio starts." />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <NavigateNext color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="After answering a question, click 'Next' to proceed." />
                  </ListItem>
                </List>

                <Alert
                  severity={isSimulation ? "warning" : "info"}
                  sx={{ mt: 2, mb: 2 }}
                >
                  <Typography variant="body2">
                    You {isSimulation ? "cannot" : "can"} return to previous
                    questions once you move forward.
                  </Typography>
                </Alert>

                <Typography variant="body1">
                  • Make sure to complete all six questions before the timer
                  runs out.
                  <br />• When you're ready, click the 'Next' button to proceed
                  to the Listening Part B.
                </Typography>
              </Box>
            ),
          };
        }

        if (part === "C") {
          return {
            title: "Part C – Multiple Choice (Healthcare Presentations)",
            content: (
              <Box>
                <Typography variant="body1" paragraph>
                  You will hear two longer extracts, where healthcare
                  professionals discuss aspects of their work.
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircle color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="Choose the best answer (A, B, or C) for each question." />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <NavigateBefore color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="You can navigate questions using the red numbered tabs, or the 'Next' and 'Back' buttons during the reading time." />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <VolumeUp color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="This navigation will not affect the audio playback." />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <Timer color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="Complete your answers as you listen." />
                  </ListItem>
                </List>

                <Typography variant="body1" sx={{ mt: 2 }}>
                  When you're ready, click the 'Next' button to proceed to the
                  Listening Part C.
                </Typography>
              </Box>
            ),
          };
        }
        break;

      case "reading":
        if (!part) {
          // General reading introduction (if needed)
          return {
            title: "Reading Sub-Test – Introduction (Acadimyst LMS)",
            content: (
              <Box>
                <Typography variant="body1" paragraph>
                  You are about to begin the Reading sub-test, which is divided
                  into three parts: Part A, Part B, and Part C.
                </Typography>
                <Typography variant="body1">
                  Click 'Next' to proceed to Part A instructions.
                </Typography>
              </Box>
            ),
          };
        }

        if (part === "A") {
          return {
            title: "Reading Part A – Skim and Scan Task",
            content: (
              <Box>
                <Typography variant="body1" paragraph>
                  You are about to begin Part A of the Reading sub-test.
                </Typography>

                <Typography variant="h6" gutterBottom>
                  Part A – Skim and Scan Task
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon>
                      <Timer color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="You will have 15 minutes to complete this section." />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <Info color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="Use the texts on the left to answer the questions on the right." />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircle color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="Each correct answer earns one mark." />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <Warning color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="You must attempt all questions — there are no penalties for incorrect responses." />
                  </ListItem>
                </List>

                <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
                  <Typography variant="body2">
                    As soon as you click 'Next', the 15-minute timer will begin
                    automatically.
                  </Typography>
                </Alert>

                <Typography variant="body1">
                  When you're ready, click the 'Next' button to begin Part A.
                </Typography>
              </Box>
            ),
          };
        }

        if (part === "BC" || part === "B") {
          return {
            title: "Reading Parts B & C – Multiple Choice & Paragraph Matching",
            content: (
              <Box>
                <Typography variant="body1" paragraph>
                  You are about to begin Parts B and C of the Reading sub-test.
                </Typography>

                <Typography variant="h6" gutterBottom>
                  Parts B and C – Multiple Choice & Paragraph Matching
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon>
                      <Timer color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="You will have 45 minutes to complete both Parts B and C combined." />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <NavigateNext color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="You may freely navigate through all questions in these sections using the red numbered tabs, or by clicking 'Next' and 'Back'." />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircle color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="Each correct answer earns one mark." />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <Warning color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="Make sure to manage your time effectively and attempt all questions." />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <Warning color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="There are no penalties for incorrect responses — attempt all questions." />
                  </ListItem>
                </List>

                <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
                  <Typography variant="body2">
                    The 45-minute timer will begin as soon as you click 'Next'.
                  </Typography>
                </Alert>

                <Typography variant="body1">
                  When you're ready, click the 'Next' button to begin Parts B &
                  C.
                </Typography>
              </Box>
            ),
          };
        }
        break;

      case "writing":
        return {
          title: "Writing Sub-Test – Introduction (Acadimyst LMS)",
          content: (
            <Box>
              <Typography variant="body1" paragraph>
                You are about to begin the OET Writing sub-test.
              </Typography>

              <Typography variant="h6" gutterBottom>
                Time Allocation
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <Timer color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Reading Time: 5 minutes" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Timer color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Writing Time: 40 minutes" />
                </ListItem>
              </List>

              <Divider sx={{ my: 2 }} />

              <Typography variant="h6" gutterBottom>
                Reading Time (First 5 Minutes)
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <Info color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="You can review the case notes and writing task." />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Highlight color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      isSimulation
                        ? "You cannot highlight or type during this period."
                        : "You can highlight or type during this period."
                    }
                    secondary={`${mode} mode`}
                  />
                </ListItem>
              </List>

              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                Submission Options
              </Typography>
              <Box sx={{ p: 2, bgcolor: "grey.100", borderRadius: 1, mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Paper Based
                </Typography>
                <Typography variant="body2" paragraph>
                  If you select the Paper-Based mode, you will be prompted to
                  upload your handwritten response. Only two pages are allowed
                  for upload. Please ensure that you upload the correct and
                  relevant writing task response. Submitting unrelated or
                  incorrect files will result in no feedback being provided.
                </Typography>
              </Box>

              <Box sx={{ p: 2, bgcolor: "grey.100", borderRadius: 1, mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Computer Based
                </Typography>
                <Typography variant="body2" paragraph>
                  If you select the Computer-Based mode, you are required to
                  type your response directly into the provided writing window.
                  Ensure your answer is complete and relevant to the given task.
                  Submissions that are off-topic or incomplete may result in no
                  feedback being provided.
                </Typography>
              </Box>

              <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
                <Typography variant="body2">
                  If you click 'Next' before the 5 minutes are over, you'll
                  immediately proceed to the Writing task.
                </Typography>
              </Alert>

              <Typography variant="h6" gutterBottom>
                Writing Time (Next 40 Minutes)
              </Typography>
              <Typography variant="body1" paragraph>
                After the reading time ends, the 40-minute timer will begin
                automatically. You can highlight text and type your response in
                the provided writing area.
              </Typography>

              <Alert severity="warning" sx={{ mt: 2, mb: 2 }}>
                <Typography variant="body2">
                  <strong>Submitting Your Test:</strong>
                  <br />• Once you've completed your response, click "Finish
                  Test" located at the top right of the screen.
                  <br />• You cannot return to the test after finishing.
                </Typography>
              </Alert>

              <Typography variant="body1">
                Click the 'Next' button to begin the Writing sub-test.
              </Typography>
            </Box>
          ),
        };

      case "speaking":
        return {
          title: "OET Speaking Test – Role Play Guidelines (Acadimyst LMS)",
          content: (
            <Box>
              <Typography variant="body1" paragraph>
                You will complete two role play cards as part of the OET
                Speaking sub-test.
              </Typography>

              <Typography variant="h6" gutterBottom>
                For each card, you are given:
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <Timer color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="3 minutes to prepare" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <VolumeUp color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="6 minutes to record your response" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircle color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Mic Test" />
                </ListItem>
              </List>

              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                You have two submission options:
              </Typography>

              <Box sx={{ p: 2, bgcolor: "grey.100", borderRadius: 1, mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Option 1: Upload Pre-recorded Audio
                </Typography>
                <Typography variant="body2">
                  • You can upload one audio file per card (maximum length: 6
                  minutes).
                  <br />• Please do not upload both card responses together.
                  Each audio must be submitted separately.
                  <br />• Ensure that the audio is clear and relevant to the
                  role play prompt.
                </Typography>
              </Box>

              <Box sx={{ p: 2, bgcolor: "grey.100", borderRadius: 1, mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Option 2: Record Live on Portal
                </Typography>
                <Typography variant="body2">
                  • You can record your response directly on the portal
                  (maximum: 6 minutes per card).
                  <br />• After recording, listen to your audio to confirm it
                  was recorded correctly.
                </Typography>
              </Box>

              <Alert severity="warning" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>Final Submission:</strong>
                  <br />• Once you have completed both responses (either by
                  upload or live recording), click Submit to send your answers
                  for review.
                  <br />• Ensure both audio files are complete and correctly
                  assigned to their respective cards.
                </Typography>
              </Alert>
            </Box>
          ),
        };

      default:
        return {
          title: "Instructions",
          content: (
            <Typography>No instructions available for this section.</Typography>
          ),
        };
    }
  };

  const { title, content } = getInstructionContent();

  return (
    <Dialog
      open={open}
      onClose={undefined} // Disable click-outside-to-close
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown={true} // Disable ESC key to close
      PaperProps={{
        sx: {
          minHeight: "60vh",
          maxHeight: "90vh",
        },
      }}
    >
      <DialogTitle sx={{ bgcolor: "#9D2235", color: "white" }}>
        <Typography variant="h6">{title}</Typography>
        {/* Show countdown for all instruction popups */}
        {shouldAutoClose && (
          <Box
            sx={{
              position: "absolute",
              right: 24,
              top: 16,
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Timer sx={{ color: "white", fontSize: 32 }} />
            <Typography
              variant="h5"
              sx={{ color: "white", fontWeight: 700, fontSize: 28 }}
            >
              {countdown}s
            </Typography>
          </Box>
        )}
      </DialogTitle>
      <DialogContent sx={{ pt: 3 }}>{content}</DialogContent>
      <DialogActions sx={{ p: 3, bgcolor: "grey.50" }}>
        <Button
          onClick={onNext}
          variant="contained"
          sx={{ bgcolor: "#9D2235" }}
        >
          Next
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InstructionModal;
